// Shared public Promotion post capabilities, optional-field validation and text assembly.
// Depends only on the public size manifest; safe for browser and Functions use.
// API-supported draft metadata does not imply an implemented publishing adapter.
const sizes = require("./promotion_image_sizes.json");

const DOCS = {
  facebook: "https://developers.facebook.com/docs/graph-api/reference/page/feed/",
  instagram: "https://developers.facebook.com/docs/instagram-platform/content-publishing/",
  linkedin: "https://learn.microsoft.com/en-us/linkedin/marketing/community-management/shares/post-api-schema",
  tiktok: "https://developers.tiktok.com/docs/en/content-posting-api-reference-direct-post",
};
const field = (key, label, type, help, constraints = {}) => ({ key, label, type, help, ...constraints });
const link = () => field("linkUrl", "Destination link", "url", "Public HTTPS URL. The platform controls the resulting link preview.", { maxLength: 2048 });
const cover = () => field("coverTimestampMs", "Cover frame (milliseconds)", "integer", "Select a frame in the uploaded video. LGV draft maximum: 60,000 ms.", { min: 0, max: 60000 });
const location = () => field("locationId", "Instagram location Page ID", "string", "Use a real numeric location Page ID, not a location name.", { format: "numericId", maxLength: 32 });
const reshare = () => field("disableReshare", "Disable resharing", "boolean", "Sets LinkedIn's isReshareDisabledByAuthor option.");
const imageAlts = (maxItems) => field("imageAltTexts", "Image alternative text", "stringArray", "One description per image, in upload order. Blank slots omit a description. LGV draft maximum: 1,000 characters per image.", { minItems: 1, maxItems, maxItemLength: 1000, allowEmptyItems: true });

function resolveBaseType(platform, postType) {
  if (typeof platform !== "string" || typeof postType !== "string" || !Object.prototype.hasOwnProperty.call(sizes, platform)) return "";
  const formats = sizes[platform];
  if (Object.prototype.hasOwnProperty.call(formats, postType)) return postType;
  const match = /^([a-z_]+)__(\d+)x(\d+)$/.exec(postType);
  if (!match || !Object.prototype.hasOwnProperty.call(formats, match[1])) return "";
  return formats[match[1]].some((size) => `${size.width}` === match[2] && `${size.height}` === match[3]) ? match[1] : "";
}

function getPromotionPostCapabilities(platform, postType) {
  const baseType = resolveBaseType(platform, postType);
  const supported = Boolean(baseType) && baseType !== "celebration";
  const supportsCaption = supported && !["story", "video_story"].includes(baseType);
  // Rich Facebook video formats have distinct provider fields. Use a smaller
  // LGV draft cap rather than claiming the feed-message maximum applies there.
  const captionLimit = !supportsCaption ? 0 : ({ facebook: ["reel", "video"].includes(baseType) ? 2200 : 63206, instagram: 2200, linkedin: 3000, tiktok: baseType === "photo_post" ? 4000 : 2200 })[platform];
  const fields = [];
  if (supported && platform === "facebook" && ["feed_text", "link"].includes(baseType)) fields.push(link());
  if (supported && platform === "facebook" && ["feed_image", "multi_image"].includes(baseType)) fields.push(imageAlts(baseType === "feed_image" ? 1 : 10));
  if (supported && platform === "instagram") {
    if (["feed_image", "feed_carousel", "reel"].includes(baseType)) fields.push(location());
    if (["feed_image", "feed_carousel"].includes(baseType)) fields.push(imageAlts(baseType === "feed_image" ? 1 : 10));
    if (baseType === "reel") fields.push(field("shareToFeed", "Also share Reel to feed", "boolean", "Share the Reel to the Instagram feed as well."), cover());
  }
  if (supported && platform === "linkedin") {
    fields.push(reshare());
    if (["feed_image", "multi_image"].includes(baseType)) fields.push(imageAlts(baseType === "feed_image" ? 1 : 20));
    if (baseType === "article") fields.push(link(), field("articleTitle", "Article preview title", "string", "Custom article title. LGV draft maximum: 1,024 characters.", { maxLength: 1024 }), field("articleDescription", "Article preview description", "string", "Custom article description. LGV draft maximum: 2,000 characters.", { maxLength: 2000 }));
    if (baseType === "document") fields.push(field("documentTitle", "Document title", "string", "LinkedIn requires a document title when publishing. LGV draft maximum: 200 characters.", { maxLength: 200 }));
    if (baseType === "reshare") fields.push(field("parentPostUrn", "Original LinkedIn post URN", "string", "The original urn:li:share:ID or urn:li:ugcPost:ID to reshare.", { format: "postUrn", maxLength: 128 }));
    if (baseType === "poll") fields.push(
      field("pollQuestion", "Poll question", "string", "Question shown in the LinkedIn poll, up to 140 characters.", { maxLength: 140 }),
      field("pollOptions", "Poll choices", "stringArray", "Provide two to four distinct choices, up to 30 characters each.", { minItems: 2, maxItems: 4, maxItemLength: 30 }),
      field("pollDurationDays", "Poll duration (days)", "integer", "LinkedIn supports one, three, seven or fourteen days.", { min: 1, max: 14, options: [1, 3, 7, 14] })
    );
  }
  if (supported && platform === "tiktok") {
    fields.push(field("disableComments", "Disable comments", "boolean", "Creator-account restrictions still apply."));
    if (baseType === "short_video") fields.push(field("disableDuet", "Disable Duet", "boolean", "Creator-account restrictions still apply."), field("disableStitch", "Disable Stitch", "boolean", "Creator-account restrictions still apply."), cover());
    if (baseType === "photo_post") fields.push(field("photoTitle", "Photo post title", "string", "Optional photo title, up to 90 characters.", { maxLength: 90 }), field("autoAddMusic", "Add recommended music", "boolean", "TikTok chooses the recommended music; this does not select a song."), field("coverImageIndex", "Cover image number (starting at 0)", "integer", "Choose an uploaded photo by its zero-based position.", { min: 0, max: 34 }));
  }
  return { supported, baseType, supportsCaption, supportsHashtags: supportsCaption, hashtagLimit: platform === "instagram" ? 5 : 30, captionLimit, fields, docsUrl: DOCS[platform] || "" };
}

function isEmpty(value) { return value === undefined || value === null || (typeof value === "string" && !value.trim()); }

function validateOption(definition, value) {
  const { label, type, maxLength, min, max, format } = definition;
  if (type === "boolean") {
    if (typeof value !== "boolean") throw new Error(`${label} must be true or false.`);
    return value;
  }
  if (type === "integer") {
    if (!Number.isSafeInteger(value) || value < min || value > max) throw new Error(`${label} must be a whole number between ${min} and ${max}.`);
    if (definition.options && !definition.options.includes(value)) throw new Error(`${label} must be one of ${definition.options.join(", ")}.`);
    return value;
  }
  if (type === "stringArray") {
    if (!Array.isArray(value) || value.length < definition.minItems || value.length > definition.maxItems) throw new Error(`${label} must contain between ${definition.minItems} and ${definition.maxItems} entries.`);
    const entries = value.map((entry) => {
      if (typeof entry !== "string") throw new Error(`${label} entries must be text.`);
      const text = entry.trim();
      if ((!definition.allowEmptyItems && !text) || text.length > definition.maxItemLength) throw new Error(`${label} entries must contain ${definition.allowEmptyItems ? "up to" : "1 to"} ${definition.maxItemLength} characters.`);
      return text;
    });
    if (!definition.allowEmptyItems && new Set(entries.map((entry) => entry.toLowerCase())).size !== entries.length) throw new Error(`${label} entries must be distinct.`);
    return entries;
  }
  if (typeof value !== "string") throw new Error(`${label} must be text.`);
  const text = value.trim();
  if (maxLength && text.length > maxLength) throw new Error(`${label} must be no longer than ${maxLength} characters.`);
  if (format === "numericId" && !/^\d+$/.test(text)) throw new Error(`${label} must be a numeric ID.`);
  if (format === "postUrn" && !/^urn:li:(share|ugcPost):\d+$/.test(text)) throw new Error(`${label} must be a LinkedIn share or ugcPost URN.`);
  if (type === "url") {
    let url;
    try { url = new URL(text); } catch { throw new Error(`${label} must be a public HTTPS URL.`); }
    const host = url.hostname.toLowerCase();
    if (url.protocol !== "https:" || url.username || url.password || !host.includes(".") ||
        host === "localhost" || /\.(localhost|local|internal)$/.test(host) || /^\d+\.\d+\.\d+\.\d+$/.test(host) || host.startsWith("[")) {
      throw new Error(`${label} must be a public HTTPS URL without credentials.`);
    }
    return url.href;
  }
  return text;
}

function normalizePromotionPostOptions(platform, postType, input, { strict = true, imageCount, videoDurationSeconds } = {}) {
  if (input === undefined || input === null) return {};
  if (typeof input !== "object" || Array.isArray(input) || ![Object.prototype, null].includes(Object.getPrototypeOf(input))) {
    if (!strict) return {};
    throw new Error("Post options must be an object.");
  }
  const capabilities = getPromotionPostCapabilities(platform, postType);
  const allowed = new Map(capabilities.fields.map((definition) => [definition.key, definition]));
  const output = {};
  for (const [key, value] of Object.entries(input)) {
    if (isEmpty(value) || (Array.isArray(value) && value.length === 0)) continue;
    const definition = allowed.get(key);
    if (!definition) {
      if (strict) throw new Error(`Post option ${key} is not supported for this Platform and Post Type.`);
      continue;
    }
    try {
      const normalized = validateOption(definition, value);
      if (!isEmpty(normalized)) output[key] = normalized;
    } catch (error) {
      if (strict) throw error;
    }
  }
  const contextError = (key, message) => { if (strict) throw new Error(message); delete output[key]; };
  if (output.imageAltTexts && Number.isInteger(imageCount) && output.imageAltTexts.length !== imageCount) contextError("imageAltTexts", "Image alternative text must match the uploaded image count and order.");
  if (output.coverImageIndex !== undefined && Number.isInteger(imageCount) && output.coverImageIndex >= imageCount) contextError("coverImageIndex", "Cover image number must refer to an uploaded image.");
  if (output.coverTimestampMs !== undefined && Number.isFinite(videoDurationSeconds) && output.coverTimestampMs >= videoDurationSeconds * 1000) contextError("coverTimestampMs", "Cover frame must be before the end of the uploaded video.");
  const pollKeys = ["pollQuestion", "pollOptions", "pollDurationDays"];
  if (pollKeys.some((key) => output[key] !== undefined) && !pollKeys.every((key) => output[key] !== undefined)) {
    if (strict) throw new Error("Complete the poll question, choices and duration together, or leave all poll fields blank.");
    pollKeys.forEach((key) => { delete output[key]; });
  }
  return output;
}

function normalizePromotionHashtags(platform, postType, input) {
  const capabilities = getPromotionPostCapabilities(platform, postType);
  if (!capabilities.supportsHashtags) return [];
  if (input === undefined || input === null || input === "") return [];
  if (!Array.isArray(input) && typeof input !== "string") throw new Error("Hashtags must be text or a list.");
  const values = typeof input === "string" ? input.split(/\s+/).filter(Boolean) : input;
  const seen = new Set();
  const output = [];
  for (const value of values) {
    if (typeof value !== "string") throw new Error("Each hashtag must be text.");
    const hashtag = value.trim().replace(/^#?/, "#");
    if (!/^#[\p{L}\p{N}_]+$/u.test(hashtag) || [...hashtag.slice(1)].length > 100) throw new Error("Use hashtags containing letters, numbers or underscores, up to 100 characters each.");
    const key = hashtag.toLowerCase();
    if (!seen.has(key)) { seen.add(key); output.push(hashtag); }
  }
  if (output.length > capabilities.hashtagLimit) throw new Error(`LGV allows up to ${capabilities.hashtagLimit} hashtags for this draft.`);
  return output;
}

function buildPromotionPostText(post = {}) {
  const capabilities = getPromotionPostCapabilities(post.platform, post.postType);
  if (!capabilities.supported) throw new Error("Choose a supported Platform and Post Type.");
  normalizePromotionPostOptions(post.platform, post.postType, post.postOptions);
  if (!capabilities.supportsCaption) return "";
  const title = String(post.title || "").trim();
  const body = String(post.body || post.postText || "").trim();
  const cta = String(post.cta || "").trim();
  const parts = [title, body].filter(Boolean);
  if (cta && !parts.some((part) => part.includes(cta))) parts.push(cta);
  const existing = new Set((parts.join("\n\n").match(/#[\p{L}\p{N}_]+/gu) || []).map((tag) => tag.toLowerCase()));
  const hashtags = normalizePromotionHashtags(post.platform, post.postType, post.hashtags).filter((tag) => !existing.has(tag.toLowerCase()));
  // One budget applies to the final caption, including inline title/body/CTA
  // tags. Repeated tags with different casing consume only one distinct slot.
  if (existing.size + hashtags.length > capabilities.hashtagLimit) throw new Error(`LGV allows up to ${capabilities.hashtagLimit} distinct hashtags across post text and the Hashtags field.`);
  if (hashtags.length) parts.push(hashtags.join(" "));
  const message = parts.join("\n\n");
  if (message.length > capabilities.captionLimit) throw new Error(`Post text including call to action and hashtags exceeds ${capabilities.captionLimit} characters.`);
  return message;
}

module.exports = { getPromotionPostCapabilities, normalizePromotionPostOptions, normalizePromotionHashtags, buildPromotionPostText };
